import { Box } from 'vendor/material';
import { Banner } from '@tackle-io/platform-ui';
import { CurrencyCode, formatValueWithCurrencyPrefix } from 'utils/currency';
import useCoSellSfCanvasActions from 'packages/salesforce-canvas/cosell/src/hooks/useCoSellSfCanvasActions';
import { DisplayCloudType } from 'packages/cosell/src/types/enums';
import FormattedDateTitle from '../components/FormattedDateTitle';
import { TextWithCloudIconPrefix } from '../../TextWithCloudIconPrefix/TextWithCloudIconPrefix';
import Summary from '../components/SummaryData';
import type { ReactNode } from 'react';
import { memo } from 'react';
import WidgetButton from '../components/WidgetButton';
import { formatIsoToDateOnlyString } from 'packages/cosell/src/utilities/formatDate';

const TEMP_ERROR_MESSAGE =
  'One or more errors happened when submitting to the cloud.';

const CurrencyDisplay = memo(
  ({
    amount,
    currency = 'USD' as CurrencyCode,
  }: {
    amount: number;
    currency?: CurrencyCode;
  }) => {
    return (
      <>
        {currency} {formatValueWithCurrencyPrefix(amount, currency, '0,0.00')}
      </>
    );
  },
);

/** displays a summary of the MSFT referral
 *  TODO: add the currency to the props when it is available from the referrals list api
 */
export default function ReferralSummary({
  dealType,
  dealValue,
  hasCloudErrors,
  name,
  status,
  substatus,
  tackleId,
  targetCloseDate,
  updatedDateTime,
}: {
  dealType?: string;
  dealValue?: number;
  hasCloudErrors?: boolean;
  name: string;
  status: string;
  substatus: string;
  updatedDateTime: string;
  targetCloseDate?: string;
  tackleId: string;
}) {
  const { handleCoSellAction } = useCoSellSfCanvasActions();
  const dataRows: { title: string; content: ReactNode }[][] = [
    [
      {
        title: 'Co-sell name',
        content: (
          <TextWithCloudIconPrefix
            icon={{ provider: DisplayCloudType.MICROSOFT }}
          >
            {name}
          </TextWithCloudIconPrefix>
        ),
      },
    ],
    [
      {
        title: 'Referral status',
        content: <Summary.Tag>{status}</Summary.Tag>,
      },
      {
        title: 'Referral substatus',
        content: <Summary.Tag>{substatus}</Summary.Tag>,
      },
    ],
    [
      {
        title: 'Last updated',
        content: <FormattedDateTitle date={updatedDateTime} />,
      },
      {
        title: 'Estimated close date',
        content: targetCloseDate
          ? formatIsoToDateOnlyString(targetCloseDate)
          : '-',
      },
    ],
    [
      {
        title: 'Co-sell type',
        content: <Summary.Tag>{dealType}</Summary.Tag>,
      },
      {
        title: 'Estimated value',
        content: <CurrencyDisplay amount={dealValue} />,
      },
    ],
  ];

  return (
    <Box style={{ display: 'grid', gap: '1rem', minHeight: '250px' }}>
      {!!hasCloudErrors && tackleId && (
        <Banner
          borderPosition="top"
          isCollapsible
          title={TEMP_ERROR_MESSAGE}
          type="danger"
        />
      )}
      <Summary>
        {dataRows.map((row, index) => (
          <Summary.Row key={index}>
            {row.map((item, idx) => (
              <Summary.Detail key={idx} title={item.title}>
                {item.content}
              </Summary.Detail>
            ))}
          </Summary.Row>
        ))}
      </Summary>

      <WidgetButton
        variant="outlined"
        color="primary"
        size="small"
        onClick={() =>
          handleCoSellAction({
            id: 'view-cosell',
            cloud: DisplayCloudType.MICROSOFT,
            tackleId,
          })
        }
      >
        View co-sell
      </WidgetButton>
    </Box>
  );
}
